import { useParams } from "react-router-dom";
import { Box, useTheme } from "@mui/material"
import {
    NavigateNext as NavigateNextIcon,
    Delete as DeleteIcon,
    Add as AddIcon
} from '@mui/icons-material';
import { v4 as uuidv4 } from 'uuid';
import { useMemo } from 'react';

import { useMSALPayload } from "@initia-digital/auth-client-utils";
import {
    BasicLoader,
    CriticalOrSiteSpecificHazardListItemEditable,
    FilledButton,
    IconButton,
    PlanRegisterHazardListItemReadOnly,
    Typography
} from "@initia-digital/component-library"
import { baseDefinedAndNotNullNarrowing } from "@initia-digital/ts-utils";
import { IHazardClass, IPlanRegisterHazard } from "@initia-digital/types/work-and-safety";

import { PlanActions } from "./PlanActions"
import { useWorkAndSafetyStore } from "../store";
import { useFullTeamList, useHazardClassList, useHazardList } from "../hooks";
import { IPlanStageProps } from "../interfaces/componentInterfaces"
import { ActiveHazardAutoCompleteMultiSelect, ActiveHazardClassAutoCompleteMultiSelect } from "./Autosuggest";
import { sortHazardClassesByPresentationOrder, sortPlanRegisterHazardClassPresentationOrder } from "../utils";
import { compareHazardsByRiskLevel } from "@initia-digital/data-service-mapping";
import { SectionHeader } from "./SectionHeader";
import { PlanHeader } from "./PlanHeader";


export const HazardAssessment = ({
    handleSave, handleSaveAndNext,
    handleStatusChange
}: IPlanStageProps) => {
    // ---------------------
    const { editPlanState, updateEditPlanState } = useWorkAndSafetyStore();

    const {
        title, status,
        criticalHazards, hazardsFromRegister
    } = editPlanState;

    const theme = useTheme();

    const params = useParams();
    const { jobId, planId } = params;

    const authDetail = useMSALPayload();

    // DATA INTEGRATION
    const { fullTeamListData } = useFullTeamList(authDetail, true);
    const { hazardListLoading, hazardListData } = useHazardList(authDetail, true, { status: 'ACTIVE' });
    const { hazardClassListLoading, hazardClassListData } = useHazardClassList(authDetail, true, { status: 'ACTIVE' });
    const hazardListAndHazardClassListAreDefined = baseDefinedAndNotNullNarrowing(hazardClassListData) && baseDefinedAndNotNullNarrowing(hazardListData);
    
    const selectedHazardClasses = useMemo(() => {
        // ---------------------
        if (!hazardListAndHazardClassListAreDefined) return [];
        
        return hazardClassListData.filter(hazardClass => {
            const hazardsInClass = hazardListData.filter(hazard => hazard.hazardClass.value === hazardClass.value);
            const selectedHazardsInClass = hazardsFromRegister.filter(hazard => hazard.hazardClass.value === hazardClass.value);
            return hazardsInClass.length > 0 && hazardsInClass.length === selectedHazardsInClass.length;
        });
    }, [hazardListAndHazardClassListAreDefined, hazardClassListData, hazardListData, hazardsFromRegister]);

    const referenceHazardClassPresentationOrder = useMemo(() => {
        // ---------------------
        if (!hazardListAndHazardClassListAreDefined) return [];
        return [...hazardClassListData].sort(sortHazardClassesByPresentationOrder).map(hazardClass => hazardClass.label);
    }, [hazardListAndHazardClassListAreDefined, hazardClassListData]);

    const registerHazardsForPresentation = useMemo(() => {
        // ---------------------
        if (!hazardListAndHazardClassListAreDefined) return [];
        return [...hazardsFromRegister]
            .sort((a, b) => compareHazardsByRiskLevel(a, b))
            .sort((a, b) => sortPlanRegisterHazardClassPresentationOrder(a, b, referenceHazardClassPresentationOrder));
    }, [hazardListAndHazardClassListAreDefined, hazardsFromRegister, referenceHazardClassPresentationOrder]);

    return (
        <Box sx={{ width: '100%' }}>
            <PlanHeader
                id={planId}
                title={title}
                status={status}
            />
            <Box
                display="flex" flexDirection="row" flexWrap="wrap"
                alignItems="center" justifyContent="space-between"
                maxWidth="100%"
            >
                <SectionHeader label="Hazard Assessment" />
                <PlanActions
                    handleSave={handleSave}
                    handleStatusChange={handleStatusChange}
                />
            </Box>
            <Box display="flex" flexDirection="column" mt="10px" mb="25px">
                <Box>
                    <Typography
                        fontSize={"0.9rem"}
                        fontWeight={700}
                        color={theme.palette.primary.contrastText}
                    >
                        Highlighted or site specific hazards
                    </Typography>
                </Box>
                <Box display="flex" flexDirection="row" mt={1} mb={2}>
                    <FilledButton
                        startIcon={<AddIcon />}
                        onClick={() => {
                            // ---------------------
                            updateEditPlanState({
                                ...editPlanState,
                                criticalHazards: [
                                    ...criticalHazards,
                                    {
                                        id: uuidv4(),
                                        hazardName: '',
                                        description: '',
                                        hazardControls: [],
                                        tags: [],
                                        jobId: jobId ?? '',
                                        planId: planId ?? ''
                                    }
                                ]
                            });
                        }}
                    >
                        New
                    </FilledButton>
                </Box>
                <Box
                    display="flex" flexDirection="column" rowGap={1}
                >
                {
                    criticalHazards.length > 0 &&
                    criticalHazards.map(criticalHazard => {
                        // --------------------------
                        return (
                            <CriticalOrSiteSpecificHazardListItemEditable
                                key={criticalHazard.id}
                                hazard={criticalHazard}
                                staffList={fullTeamListData}
                                actionButtons={[
                                    <IconButton
                                        size="small"
                                        tooltipTitle="Delete hazard"
                                        onClick={() => {
                                            // ---------------------
                                            updateEditPlanState({
                                                ...editPlanState,
                                                criticalHazards: editPlanState.criticalHazards.filter(hazard => hazard.id !== criticalHazard.id)
                                            });
                                        }}
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                ]}
                                handleHazardChange={(updatedHazard) => {
                                    // ---------------------
                                    updateEditPlanState({
                                        ...editPlanState,
                                        criticalHazards: editPlanState.criticalHazards.map(hazard =>
                                            hazard.id === updatedHazard.id ? updatedHazard : hazard
                                        )
                                    });
                                }}
                            />
                        )
                    })
                }
                {
                    criticalHazards.length === 0 &&
                    <Box display="flex" flexDirection="row" mt="25px" width="100%" justifyContent="center">
                        <Typography
                            fontSize={"0.9rem"}
                            fontWeight={300}
                            color={theme.palette.primary.contrastText}
                        >
                            No critical or site specific hazards recorded
                        </Typography>
                    </Box>
                }
                </Box>
            </Box>
            <Box display="flex" flexDirection="column" mt="10px" mb="25px">
                <Box>
                    <Typography
                        fontSize={"0.9rem"}
                        fontWeight={700}
                        color={theme.palette.primary.contrastText}
                    >
                        From the hazard register
                    </Typography>
                </Box>
                {
                    hazardListLoading && hazardClassListLoading &&
                    <Box display="flex" flexDirection="row" mt="25px" width="100%" justifyContent="center">
                        <BasicLoader 
                            size={50}
                        />
                    </Box>
                }
                {
                    !hazardListLoading && !hazardClassListLoading &&
                    hazardListAndHazardClassListAreDefined &&
                    <>
                        <Box>
                            <Typography
                                fontSize={"0.7rem"}
                                fontWeight={300}
                                color={theme.palette.primary.contrastText}
                            >
                                Bulk add by hazard group
                            </Typography>
                        </Box>
                        <Box>
                            <ActiveHazardClassAutoCompleteMultiSelect
                                name={`hazardClassFromRegister`}
                                placeholder={`Add hazard groups`}
                                handleAutoSuggestSelectStateChange={(event) => {
                                    // ---------------------
                                    const hazardClass = event.target.value as IHazardClass;
                                    const selectedRegisterHazards = hazardListData.filter(hazard => hazard.hazardClass.value === hazardClass.value);
                                    
                                    const isAllHazardClassSelected = hazardsFromRegister.filter(hazard => hazard.hazardClass.value === hazardClass.value).length === selectedRegisterHazards.length;
                                    
                                    // If all hazard class is selected, treat selection as deactivation
                                    if (isAllHazardClassSelected) {
                                        // --------------------------------------
                                        const hazardUpdate = [...hazardsFromRegister.filter(hazard => hazard.hazardClass.value !== hazardClass.value)];
                                        updateEditPlanState({
                                            ...editPlanState,
                                            hazardsFromRegister: hazardUpdate
                                        });
                                    }
                                    else {
                                        // Otherwise add the selected hazards to the list (which haven't already been added individually)
                                        // --------------------------------------
                                        const hazardUpdate = [...hazardsFromRegister, ...selectedRegisterHazards.filter(selectedHazard => {
                                            // ----------------------------------
                                            const existingHazard = hazardsFromRegister.find(hazard => hazard.hazardId === selectedHazard.id);
                                            return !baseDefinedAndNotNullNarrowing(existingHazard);
                                        }).map(hazardToAdd => ({
                                            ...hazardToAdd,
                                            hazardId: hazardToAdd.id,
                                            id: uuidv4()
                                        }))];
                                        updateEditPlanState({
                                            ...editPlanState,
                                            hazardsFromRegister: hazardUpdate
                                        });
                                    }
                                }}
                                selectedValues={selectedHazardClasses}
                            />
                        </Box>
                        <Box mt="8px">
                            <Typography
                                fontSize={"0.7rem"}
                                fontWeight={300}
                                color={theme.palette.primary.contrastText}
                            >
                                Or add individually
                            </Typography>
                        </Box>
                        <Box>
                        <ActiveHazardAutoCompleteMultiSelect
                            name={`hazardsFromRegister`}
                            handleAutoSuggestSelectStateChange={(event) => {
                                // ---------------------
                                const selectedHazards = event.target.value as IPlanRegisterHazard[];
                                const hazardUpdate = [
                                   ...selectedHazards.map(hazard => {
                                        if ('hazardId' in hazard) {
                                            // ----------------------------------
                                            return hazard;
                                        }
                                        else {
                                            // ----------------------------------
                                            return {
                                                ...hazard,
                                                hazardId: hazard.id, // hazard register id
                                                id: uuidv4() // instance id
                                            }
                                        }
                                   })
                                ];
                                updateEditPlanState({
                                    ...editPlanState,
                                    hazardsFromRegister: hazardUpdate
                                });
                            }}
                            selectedValues={hazardsFromRegister}
                            placeholder={`Add hazards`}
                        />
                        </Box>
                    </>
                }

                    <Box mt="8px">
                        <Typography
                            fontSize={"0.8rem"}
                            fontWeight={700}
                            color={theme.palette.secondary.main}
                        >
                            {`You have selected ${hazardsFromRegister.length} hazard${hazardsFromRegister.length === 1 ? '' : 's'} from the register`}
                        </Typography>
                    </Box>
                    <Box
                        display="flex" flexDirection="column" rowGap={1}
                    >
                    {
                        registerHazardsForPresentation.map(hazard => {
                            // ---------------------
                            return (
                                <PlanRegisterHazardListItemReadOnly
                                    key={hazard.id}
                                    hazard={hazard}
                                    actionButtons={[
                                        <IconButton
                                            size="small"
                                            tooltipTitle="Delete hazard"
                                            onClick={() => {
                                                // ---------------------
                                                updateEditPlanState({
                                                    ...editPlanState,
                                                    hazardsFromRegister: editPlanState.hazardsFromRegister.filter(stateHazard => stateHazard.id !== hazard.id)
                                                });
                                            }}
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    ]}
                                />
                            )
                        })
                    }
                    </Box>
            </Box>

            <Box
                width="100%"
                display="flex" flexDirection="row" justifyContent="flex-end"
                pt="12px"
            >
                <FilledButton
                    onClick={handleSaveAndNext}
                    endIcon={<NavigateNextIcon />}
                >
                    Save and Next
                </FilledButton>
            </Box>
        </Box>
    )
}